import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { NewsCard } from '@/components/news/NewsCard';
import { AppText } from '@/components/ui/AppText';
import { useThemePalette } from '@/config/theme';
import { useWcNews } from '@/features/news/hooks';

const CARD_WIDTH = 264;

/**
 * Match news section: a horizontally-scrolling rail of World Cup news for this
 * fixture (plus general WC news) off {@link useWcNews}. Mirrors the match
 * screen's other rails (MatchHighlights): a small section header + a horizontal
 * row of cards. Self-hides entirely while loading-empty or when nothing is
 * synced for the fixture, so it never leaves a bare header behind.
 */
export function MatchNews({ fixtureId }: { fixtureId: string }) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { data } = useWcNews(fixtureId, 12);
  const articles = data ?? [];

  if (articles.length === 0) return null;

  return (
    <View className="mb-4">
      <View className="mb-2.5 flex-row items-center gap-2 px-4">
        <Ionicons name="newspaper-outline" size={16} color={palette.voltBright} />
        <AppText variant="micro" weight="bold" className="uppercase tracking-widest text-muted">
          {t('match.news')}
        </AppText>
      </View>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ paddingHorizontal: 16, gap: 12 }}
      >
        {articles.map((article) => (
          <View key={article.id} style={{ width: CARD_WIDTH }}>
            <NewsCard article={article} />
          </View>
        ))}
      </ScrollView>
    </View>
  );
}
